<template>
  <div id="account" class="fill-height align-start">
    <v-row class="g-container" no-gutters>
      <v-col>
        <v-row class="ma-0">
          <h2>{{ $route.meta.title }}</h2>
        </v-row>

        <v-row>
          <v-col
            v-for="option in filteredMyAccountOptions"
            :key="option.title"
            cols="12"
            sm="6"
            lg="3"
            class="d-flex"
          >
            <v-card
              @click="handleRouteForOption(option)"
              class="p-card flex-grow-1 px-4 px-md-6 pt-3 pt-md-4 pb-4 pb-md-6"
            >
              <v-row align="center">
                <v-icon class="p-icon">{{ option.icon }}</v-icon>
                <span class="g-heading-xs g-bold">{{ option.title }}</span>
                <v-badge
                  v-if="option.actionableItemCount"
                  offset-x="-20"
                  color="orange"
                  :content="option.actionableItemCount"
                ></v-badge>
                <v-spacer></v-spacer>
                <v-icon v-if="option.actionIcon" class="p-icon">{{
                  option.actionIcon
                }}</v-icon>
              </v-row>
              <v-divider class="mt-2 mb-3" horizontal></v-divider>
              <div>
                <template v-if="option.subtitle"
                  >{{ option.subtitle }}<br
                /></template>
                <ul v-for="text in option.texts" :key="text">
                  <li>{{ text }}</li>
                </ul>
              </div>
              <div
                v-if="featureSubscriptionEnabled && option.premiumFeature"
                class="my-2"
              >
                <i-tag
                  :text="'Premium only'"
                  color="#253d81"
                  textColor="white"
                  full
                />
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import iTag from '@/components/Tag'

export default {
  components: {
    iTag
  },

  data: () => ({}),

  computed: {
    ...mapGetters('tenant', [
      'featureSubscriptionEnabled',
      'featureBankAccountsEnabled',
      'orderChatEnabled',
      'isILR',
      'tenantAgreementEnabled'
    ]),
    ...mapGetters('preferences', [
      'isOwnerUser',
      'isAdminUser',
      'isStandardUser'
    ]),
    ...mapGetters('retailerInvites', ['actionableConsents']),

    myAccountOptions() {
      return [
        {
          title: 'Manage Details',
          texts: ['Billing Address', 'Licences'],
          icon: 'mdi-account-circle-outline',
          routeName: 'manage-details',
          visibleToUserRoles: ['OWNER', 'ADMIN', 'STANDARD']
        },
        {
          title: 'Login & Security',
          texts: ['Account Details', 'Username / Email address', 'Password'],
          icon: 'mdi-lock-outline',
          routeName: 'login-security',
          visibleToUserRoles: ['OWNER', 'ADMIN', 'STANDARD']
        },
        {
          title: 'Multiple Account Users',
          subtitle: 'Manage Users',
          texts: ['Invite additional users', 'Set admin / standard privileges'],
          icon: 'mdi-account-plus-outline',
          routeName: 'users-management',
          visibleToUserRoles: ['OWNER', 'ADMIN', 'STANDARD'],
          premiumFeature: true
        },
        {
          title: `${this.isILR ? 'Customer' : 'Retailer'} Turn In Orders`,
          texts: [
            `${this.isILR ? 'Customer' : 'Retailer'} Management`,
            'Agent Invites & Management',
            `Approved Agents by ${this.isILR ? 'Customers' : 'Retailers'}`
          ],
          icon: 'mdi-face-agent',
          routeName: 'retailer-turn-in-orders',
          visibleToUserRoles: ['OWNER', 'ADMIN', 'STANDARD'],
          actionableItemCount: this.actionableConsents?.length || 0,
          premiumFeature: true
        },
        {
          title: 'Email Preferences',
          texts: ['Update email preferences'],
          icon: 'mdi-email-outline',
          routeName: 'email-preferences',
          visibleToUserRoles: ['OWNER', 'ADMIN', 'STANDARD']
        },
        {
          title: 'Retailer Message History',
          texts: [
            'View retained Retailer messages',
            'Reply to retailer messages'
          ],
          icon: 'mdi-message-text-outline',
          routeName: 'order-chat-history',
          visibleToUserRoles: ['OWNER', 'ADMIN', 'STANDARD'],
          visible: this.orderChatEnabled
        },
        {
          title: 'Trading Agreements',
          texts: ['Payout Days', 'Trading Terms & Fees'],
          icon: 'mdi-currency-usd',
          routeName: 'supplier-trading-agreements',
          visibleToUserRoles: ['OWNER', 'ADMIN'],
          visible: !this.isILR
        },
        {
          title: 'User Agreement',
          texts: ['View and download agreement'],
          icon: 'mdi-file-document-outline',
          routeName: 'supplier-user-agreement',
          visibleToUserRoles: ['OWNER', 'ADMIN'],
          visible: this.tenantAgreementEnabled
        },
        {
          title: 'Subscription',
          texts: ['Update subscription level', 'Credit card payment details'],
          icon: 'mdi-youtube-subscription',
          routeName: 'subscription',
          visibleToUserRoles: ['OWNER', 'ADMIN'],
          visible: this.featureSubscriptionEnabled
        },
        {
          title: 'Bank Details',
          texts: ['Manage Bank Account details'],
          icon: 'mdi-bank-outline',
          routeName: 'bank-details',
          actionIcon: 'mdi-arrow-top-right-thick',
          visibleToUserRoles: ['OWNER', 'ADMIN'],
          visible: this.featureBankAccountsEnabled
        }
      ]
    },

    filteredMyAccountOptions() {
      /*
        Filtering by role needs to be manually since users could have 2 or more roles
        and some roles are more important than others. Start filtering from most
        important role to the less important one
      */
      if (this.isOwnerUser) return this.filterOptionsByRole('OWNER')
      else if (this.isAdminUser) return this.filterOptionsByRole('ADMIN')
      else return this.filterOptionsByRole('STANDARD')
    }
  },

  methods: {
    filterOptionsByRole(role) {
      return this.myAccountOptions
        .filter((option) => option.visibleToUserRoles.includes(role))
        .filter((option) => option.visible !== false)
    },

    handleRouteForOption(option) {
      this.$router.push({ name: option.routeName })
    }
  }
}
</script>

<style lang="scss" scoped>
#account {
  .p-card {
    overflow: hidden;
  }
  .p-icon {
    width: 32px;
    height: 32px;
    margin-left: 8px;
    margin-right: 8px;
    color: $secondary-color;
  }
}

@media only screen and (min-width: 960px) {
  #account {
    .p-card {
      height: 195px;
    }
    .mdi:before {
      font-size: 28px;
    }
  }
}
</style>
